export type PwaPlatformFamily = 'ios' | 'android' | 'desktop' | 'unknown';

export type PwaInstallMethod = 'native_prompt' | 'ios_manual' | 'unsupported';

export type PwaInstallState = {
  isInstalled: boolean;
  canPromptInstall: boolean;
  isInstallSupported: boolean;
  platformFamily: PwaPlatformFamily;
  installMethod: PwaInstallMethod;
};

export type BuildPwaInstallStateInput = {
  userAgent: string;
  hasPromptInstallEvent: boolean;
  hasServiceWorkerSupport: boolean;
  isStandalone: boolean;
};

type StandaloneNavigator = Navigator & {
  standalone?: boolean;
};

export type BeforeInstallPromptChoice = 'accepted' | 'dismissed';

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: BeforeInstallPromptChoice;
    platform: string;
  }>;
};

export const detectPwaPlatformFamily = (userAgent: string): PwaPlatformFamily => {
  const normalizedUserAgent = userAgent.toLowerCase();
  const isiPhone = normalizedUserAgent.includes('iphone');
  const isiPad = normalizedUserAgent.includes('ipad');
  const isiPod = normalizedUserAgent.includes('ipod');
  const isIos = isiPhone || isiPad || isiPod;
  if (isIos) {
    return 'ios';
  }

  const isAndroid = normalizedUserAgent.includes('android');
  if (isAndroid) {
    return 'android';
  }

  const hasDesktopSignal =
    normalizedUserAgent.includes('macintosh') ||
    normalizedUserAgent.includes('windows') ||
    normalizedUserAgent.includes('linux');
  if (hasDesktopSignal) {
    return 'desktop';
  }

  return 'unknown';
};

export const detectStandaloneMode = (
  mediaQueryMatches: boolean,
  navigatorObject?: Navigator
): boolean => {
  if (mediaQueryMatches) {
    return true;
  }

  const standaloneNavigator = navigatorObject as StandaloneNavigator | undefined;
  const hasStandaloneFlag = standaloneNavigator?.standalone === true;
  return hasStandaloneFlag;
};

export const resolvePwaInstallMethod = (
  platformFamily: PwaPlatformFamily,
  hasPromptInstallEvent: boolean
): PwaInstallMethod => {
  if (hasPromptInstallEvent) {
    return 'native_prompt';
  }

  if (platformFamily === 'ios') {
    return 'ios_manual';
  }

  return 'unsupported';
};

export const buildPwaInstallState = ({
  userAgent,
  hasPromptInstallEvent,
  hasServiceWorkerSupport,
  isStandalone,
}: BuildPwaInstallStateInput): PwaInstallState => {
  const platformFamily = detectPwaPlatformFamily(userAgent);
  const installMethod = resolvePwaInstallMethod(platformFamily, hasPromptInstallEvent);
  const isInstallSupported = hasServiceWorkerSupport || platformFamily === 'ios';

  return {
    isInstalled: isStandalone,
    canPromptInstall: hasPromptInstallEvent,
    isInstallSupported,
    platformFamily,
    installMethod,
  };
};
